import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const ManageProducts = () => {
  const { getProducts, patchProduct, createProduct, deletePrice } = useApi();
  const { stripeAccountId, isCoach, stripeIsSetup, paymentsProEnabled } =
    useUserInfo();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [addPriceDialogOpen, setAddPriceDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedPriceUsers, setSelectedPriceUsers] = useState([]);
  const [selectedPriceName, setSelectedPriceName] = useState("");
  const [editPriceDialogOpen, setEditPriceDialogOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [priceToDelete, setPriceToDelete] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    prices: [],
  });
  const [newPrice, setNewPrice] = useState({
    amount: "",
    currency: "usd",
    description: "",
    priceType: "one-time", // "recurring" or "one-time"
    quantity: "",
    interval: "month",
    intervalCount: 1,
  });

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts(stripeAccountId);
      setProducts(data || []);
      // Reset selected tab if it's out of bounds
      const productCount = (data || []).length;
      if (productCount > 0 && selectedTab >= productCount) {
        setSelectedTab(0);
      }
      setError("");
    } catch (err) {
      console.error("Failed to load products:", err);
      setError("Unable to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [getProducts, stripeAccountId]);

  const handleEditPrice = (price) => {
    const isRecurring = !!price.recurring;
    const activeUserCount = price.activeUserCount || 0;

    setEditingPrice({
      priceId: price.priceId,
      amount: (price.unitAmount / 100).toFixed(2),
      currency: "usd",
      description: price.metadata?.description || "",
      quantity: price.metadata?.quantity || "",
      isRecurring,
      hasActiveUsers: activeUserCount > 0,
    });
    setEditPriceDialogOpen(true);
    setError("");
  };

  const handleSavePriceEdit = async () => {
    if (!editingPrice) return;

    // If there are active users, only allow updating description and quantity (for non-recurring)
    if (editingPrice.hasActiveUsers) {
      try {
        const updateData = {
          priceId: editingPrice.priceId,
          description: editingPrice.description,
          stripeAccountId,
        };
        // Only include quantity for non-recurring products
        if (!editingPrice.isRecurring) {
          updateData.quantity = editingPrice.quantity || undefined;
        }
        await patchProduct(updateData);
        setEditPriceDialogOpen(false);
        setEditingPrice(null);
        setError("");
        await loadProducts();
        return;
      } catch (err) {
        console.error("Failed to update product:", err);
        setError("Failed to update product. Please try again.");
        return;
      }
    }

    // For prices without active users, allow full editing including amount
    if (!editingPrice.amount || parseFloat(editingPrice.amount) <= 0) {
      setError("Price amount must be greater than 0.");
      return;
    }

    try {
      const updateData = {
        priceId: editingPrice.priceId,
        description: editingPrice.description,
        amount: parseFloat(editingPrice.amount),
        currency: "usd",
        recurring: editingPrice.isRecurring || false,
        stripeAccountId,
      };
      // Only include quantity for non-recurring products
      if (!editingPrice.isRecurring) {
        updateData.quantity = editingPrice.quantity || undefined;
      }
      await patchProduct(updateData);
      setEditPriceDialogOpen(false);
      setEditingPrice(null);
      setError("");
      await loadProducts();
    } catch (err) {
      console.error("Failed to update product:", err);
      if (err.message?.includes("Cannot update price")) {
        setError("Cannot update price that has active users.");
      } else {
        setError("Failed to update product. Please try again.");
      }
    }
  };

  const handleDeletePrice = (priceId, activeUserCount) => {
    if (activeUserCount > 0) {
      setError(`Cannot delete price with ${activeUserCount} active user(s).`);
      return;
    }
    setPriceToDelete({ priceId, activeUserCount });
    setDeleteConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!priceToDelete) return;

    try {
      await deletePrice({
        priceId: priceToDelete.priceId,
        stripeAccountId,
      });
      await loadProducts();
      setError("");
      setDeleteConfirmDialogOpen(false);
      setPriceToDelete(null);
    } catch (err) {
      console.error("Failed to delete price:", err);
      const errorMessage =
        err.message || "Failed to delete price. Please try again.";
      setError(errorMessage);
      setDeleteConfirmDialogOpen(false);
      setPriceToDelete(null);
    }
  };

  const handleAddPrice = () => {
    setNewProduct({
      ...newProduct,
      prices: [
        ...newProduct.prices,
        {
          amount: "",
          currency: "usd",
          description: "",
          priceType: "one-time", // "recurring" or "one-time"
          quantity: "",
          interval: "month",
          intervalCount: 1,
        },
      ],
    });
  };

  const handleRemovePrice = (index) => {
    setNewProduct({
      ...newProduct,
      prices: newProduct.prices.filter((_, i) => i !== index),
    });
  };

  const handlePriceChange = (index, field, value) => {
    const updatedPrices = [...newProduct.prices];
    updatedPrices[index] = {
      ...updatedPrices[index],
      [field]: value,
      currency: "usd", // Always set currency to USD
    };
    setNewProduct({
      ...newProduct,
      prices: updatedPrices,
    });
  };

  const handleCreateProduct = async () => {
    try {
      setError("");
      if (!newProduct.name.trim()) {
        setError("Product name is required.");
        return;
      }
      if (newProduct.prices.length === 0) {
        setError("At least one price is required.");
        return;
      }

      // Validate prices
      for (const price of newProduct.prices) {
        if (!price.amount || parseFloat(price.amount) <= 0) {
          setError("All prices must have a valid amount greater than 0.");
          return;
        }
        if (
          price.priceType === "one-time" &&
          price.quantity &&
          parseInt(price.quantity) <= 0
        ) {
          setError("Bundle size must be greater than 0 if provided.");
          return;
        }
      }

      const result = await createProduct({
        productName: newProduct.name,
        productDescription: newProduct.description,
        prices: newProduct.prices.map((p) => ({
          amount: parseFloat(p.amount),
          currency: "usd",
          description: p.description,
          quantity:
            p.priceType === "one-time" ? p.quantity || undefined : undefined,
          recurring: p.priceType === "recurring",
          interval: p.priceType === "recurring" ? p.interval : undefined,
          intervalCount:
            p.priceType === "recurring" ? p.intervalCount : undefined,
        })),
        stripeAccountId,
      });

      setCreateDialogOpen(false);
      setNewProduct({
        name: "",
        description: "",
        prices: [],
      });
      setError("");
      const updatedProducts = await getProducts(stripeAccountId);
      setProducts(updatedProducts || []);

      // Select the newly created product
      if (result?.productId && updatedProducts) {
        const newIndex = updatedProducts.findIndex(
          (p) => p.productId === result.productId
        );
        if (newIndex >= 0) {
          setSelectedTab(newIndex);
        }
      }

      // Reload to get updated metadata
      await loadProducts();
    } catch (err) {
      console.error("Failed to create product:", err);
      const errorMessage =
        err.message || "Failed to create product. Please try again.";
      setError(errorMessage);
    }
  };

  const handleAddPriceToProduct = async () => {
    try {
      setError("");
      if (!newPrice.amount || parseFloat(newPrice.amount) <= 0) {
        setError("Price amount must be greater than 0.");
        return;
      }
      if (
        newPrice.priceType === "one-time" &&
        newPrice.quantity &&
        parseInt(newPrice.quantity) <= 0
      ) {
        setError("Bundle size must be greater than 0 if provided.");
        return;
      }

      await createProduct({
        productId: selectedProductId,
        prices: [
          {
            amount: parseFloat(newPrice.amount),
            currency: "usd",
            description: newPrice.description,
            quantity:
              newPrice.priceType === "one-time"
                ? newPrice.quantity || undefined
                : undefined,
            recurring: newPrice.priceType === "recurring",
            interval:
              newPrice.priceType === "recurring"
                ? newPrice.interval
                : undefined,
            intervalCount:
              newPrice.priceType === "recurring"
                ? newPrice.intervalCount
                : undefined,
          },
        ],
        stripeAccountId,
      });

      setAddPriceDialogOpen(false);
      setSelectedProductId(null);
      setNewPrice({
        amount: "",
        currency: "usd",
        description: "",
        priceType: "one-time",
        quantity: "",
        interval: "month",
        intervalCount: 1,
      });
      setError("");
      await loadProducts();
    } catch (err) {
      console.error("Failed to add price:", err);
      const errorMessage =
        err.message || "Failed to add price. Please try again.";
      setError(errorMessage);
    }
  };

  const content = useMemo(() => {
    if (!isCoach) {
      return (
        <Alert severity="warning">
          This page is for coaches. Please sign in as a coach to manage
          products.
        </Alert>
      );
    }

    if (stripeIsSetup && !paymentsProEnabled) {
      return (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              component={Link}
              to="/coach-subscription"
            >
              Subscribe
            </Button>
          }
        >
          Payments Pro is required to create and manage products. Subscribe to
          Payments Pro to unlock this feature.
        </Alert>
      );
    }

    if (!stripeIsSetup) {
      return (
        <Alert
          severity="info"
          action={
            paymentsProEnabled && (
              <Button
                color="inherit"
                size="small"
                component={Link}
                to="/coaching/create-account"
              >
                Connect Stripe
              </Button>
            )
          }
        >
          {paymentsProEnabled
            ? "You have access to payments but need to connect your Stripe account to create and manage products."
            : "Set up your Stripe account to create and manage products."}
        </Alert>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (products.length === 0) {
      return (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Product
            </Button>
          </Box>
          <Alert severity="warning">
            No products found. Create your first product.
          </Alert>
        </Box>
      );
    }

    const selectedProduct = products[selectedTab] || products[0];

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1 }}
          >
            {products.map((product, index) => (
              <Tab key={product.productId} label={product.name} />
            ))}
          </Tabs>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ ml: 2 }}
          >
            Create Product
          </Button>
        </Box>

        {selectedProduct && (
          <Box>
            {selectedProduct.metadata?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedProduct.metadata.description}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedProductId(selectedProduct.productId);
                  setAddPriceDialogOpen(true);
                }}
              >
                Add Price
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Price</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="center">Active Users</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedProduct.prices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No prices yet. Click "Add Price" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedProduct.prices.map((price) => {
                      const isRecurring = !!price.recurring;
                      const activeUserCount = price.activeUserCount || 0;
                      const canEdit = activeUserCount === 0;
                      return (
                        <TableRow key={price.priceId}>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight="medium">
                                ${(price.unitAmount / 100).toFixed(2)}{" "}
                                {price.currency?.toUpperCase()}
                              </Typography>
                              {isRecurring && (
                                <Chip
                                  label="Recurring"
                                  size="small"
                                  color="primary"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {price.metadata?.description || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {!isRecurring ? (
                              <Typography variant="body2">
                                {price.metadata?.quantity || "-"}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight="medium">
                                {activeUserCount}
                              </Typography>
                              {activeUserCount > 0 && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedPriceUsers(
                                      price.activeUsers || []
                                    );
                                    setSelectedPriceName(
                                      `$${(price.unitAmount / 100).toFixed(
                                        2
                                      )} ${price.currency?.toUpperCase() || ""}`
                                    );
                                    setUsersDialogOpen(true);
                                  }}
                                  title="View active users"
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "center",
                              }}
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditPrice(price)}
                                title={
                                  activeUserCount > 0
                                    ? "Edit description and quantity only"
                                    : "Edit price"
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {canEdit ? (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDeletePrice(
                                      price.priceId,
                                      activeUserCount
                                    )
                                  }
                                  title="Delete price"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <Tooltip title="Can't delete price with active users">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {selectedProduct.prices.some(
              (p) => (p.activeUserCount || 0) > 0
            ) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Prices with active users can only have their description
                {selectedProduct.prices.some(
                  (p) => !p.recurring && (p.activeUserCount || 0) > 0
                ) && " and quantity"}{" "}
                edited. Amount and other settings cannot be changed. Delete
                button is only available for prices with 0 active users.
              </Alert>
            )}
          </Box>
        )}
      </Box>
    );
  }, [
    isCoach,
    stripeIsSetup,
    paymentsProEnabled,
    loading,
    products,
    stripeAccountId,
    createDialogOpen,
    selectedTab,
    addPriceDialogOpen,
    editPriceDialogOpen,
    editingPrice,
    usersDialogOpen,
    selectedPriceUsers,
    selectedPriceName,
  ]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Manage Products
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Create and manage your products and prices. Products can have multiple
        prices (e.g., monthly vs 3-month plans). Visit your{" "}
        <a href="https://dashboard.stripe.com" target="_blank">
          Stripe Dashboard
        </a>{" "}
        to view make futher edits.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {content}

      {/* Create Product Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setNewProduct({
            name: "",
            description: "",
            prices: [],
          });
          setError("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Product</DialogTitle>
        <DialogContent sx={{ overflowY: "visible" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Product Name"
            value={newProduct.name}
            onChange={(e) =>
              setNewProduct({ ...newProduct, name: e.target.value })
            }
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          <TextField
            label="Product Description (optional)"
            value={newProduct.description}
            onChange={(e) =>
              setNewProduct({ ...newProduct, description: e.target.value })
            }
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Prices
          </Typography>
          {newProduct.prices.map((price, index) => (
            <Card key={index} sx={{ mb: 2, p: 2 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="subtitle1">Price {index + 1}</Typography>
                <IconButton
                  onClick={() => handleRemovePrice(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label="Amount"
                type="number"
                value={price.amount}
                onChange={(e) =>
                  handlePriceChange(index, "amount", e.target.value)
                }
                required
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ mb: 2 }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Description (optional)"
                value={price.description}
                onChange={(e) =>
                  handlePriceChange(index, "description", e.target.value)
                }
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Price Type</InputLabel>
                <Select
                  value={price.priceType || "one-time"}
                  label="Price Type"
                  onChange={(e) =>
                    handlePriceChange(index, "priceType", e.target.value)
                  }
                >
                  <MenuItem value="recurring">Recurring</MenuItem>
                  <MenuItem value="one-time">Single Time Payment</MenuItem>
                </Select>
              </FormControl>
              {price.priceType === "recurring" ? (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Interval</InputLabel>
                    <Select
                      value={price.interval}
                      label="Interval"
                      onChange={(e) =>
                        handlePriceChange(index, "interval", e.target.value)
                      }
                    >
                      <MenuItem value="day">Day</MenuItem>
                      <MenuItem value="week">Week</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Interval Count"
                    type="number"
                    value={price.intervalCount}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "intervalCount",
                        parseInt(e.target.value) || 1
                      )
                    }
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              ) : (
                <TextField
                  label="Bundle Size (optional)"
                  type="number"
                  value={price.quantity}
                  onChange={(e) =>
                    handlePriceChange(index, "quantity", e.target.value)
                  }
                  inputProps={{ min: 1 }}
                  fullWidth
                  helperText="Number of uses/sessions included in this purchase"
                />
              )}
            </Card>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleAddPrice()}
            variant="outlined"
            fullWidth
          >
            Add Price
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewProduct({
                name: "",
                description: "",
                prices: [],
              });
              setError("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateProduct} variant="contained">
            Create Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Price Dialog */}
      <Dialog
        open={addPriceDialogOpen}
        onClose={() => {
          setAddPriceDialogOpen(false);
          setSelectedProductId(null);
          setNewPrice({
            amount: "",
            currency: "usd",
            description: "",
            priceType: "one-time",
            quantity: "",
            interval: "month",
            intervalCount: 1,
          });
          setError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Price to Product</DialogTitle>
        <DialogContent sx={{ overflowY: "visible" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Amount"
            type="number"
            value={newPrice.amount}
            onChange={(e) =>
              setNewPrice({ ...newPrice, amount: e.target.value })
            }
            required
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
          <TextField
            label="Description (optional)"
            value={newPrice.description}
            onChange={(e) =>
              setNewPrice({ ...newPrice, description: e.target.value })
            }
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Price Type</InputLabel>
            <Select
              value={newPrice.priceType || "one-time"}
              label="Price Type"
              onChange={(e) =>
                setNewPrice({ ...newPrice, priceType: e.target.value })
              }
            >
              <MenuItem value="recurring">Recurring</MenuItem>
              <MenuItem value="one-time">Single Time Payment</MenuItem>
            </Select>
          </FormControl>
          {newPrice.priceType === "recurring" ? (
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Interval</InputLabel>
                <Select
                  value={newPrice.interval}
                  label="Interval"
                  onChange={(e) =>
                    setNewPrice({ ...newPrice, interval: e.target.value })
                  }
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="year">Year</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Interval Count"
                type="number"
                value={newPrice.intervalCount}
                onChange={(e) =>
                  setNewPrice({
                    ...newPrice,
                    intervalCount: parseInt(e.target.value) || 1,
                  })
                }
                inputProps={{ min: 1 }}
                sx={{ flex: 1 }}
              />
            </Box>
          ) : (
            <TextField
              label="Bundle Size (optional)"
              type="number"
              value={newPrice.quantity}
              onChange={(e) =>
                setNewPrice({ ...newPrice, quantity: e.target.value })
              }
              inputProps={{ min: 1 }}
              fullWidth
              helperText="Number of uses/sessions included in this purchase"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddPriceDialogOpen(false);
              setSelectedProductId(null);
              setNewPrice({
                amount: "",
                currency: "usd",
                description: "",
                quantity: "",
                recurring: false,
                interval: "month",
                intervalCount: 1,
              });
              setError("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddPriceToProduct} variant="contained">
            Add Price
          </Button>
        </DialogActions>
      </Dialog>

      {/* Active Users Dialog */}
      <Dialog
        open={usersDialogOpen}
        onClose={() => setUsersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Active Users - {selectedPriceName}</DialogTitle>
        <DialogContent>
          {selectedPriceUsers.length === 0 ? (
            <Typography color="text.secondary">
              No active users found.
            </Typography>
          ) : (
            <List>
              {selectedPriceUsers.map((user, index) => (
                <ListItem key={user.userId || index} divider>
                  <ListItemText
                    primary={user.name || "Unknown User"}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                        {user.quantityLeft !== undefined &&
                          user.quantityLeft !== null && (
                            <Typography variant="body2" color="text.secondary">
                              Quantity Left: {user.quantityLeft}
                            </Typography>
                          )}
                        {user.billingDate && (
                          <Typography variant="body2" color="text.secondary">
                            Next Billing:{" "}
                            {new Date(user.billingDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Price Dialog */}
      <Dialog
        open={editPriceDialogOpen}
        onClose={() => {
          setEditPriceDialogOpen(false);
          setEditingPrice(null);
          setError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Price</DialogTitle>
        <DialogContent sx={{ overflowY: "visible" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {editingPrice && (
            <>
              {!editingPrice.hasActiveUsers && (
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <TextField
                    label="Amount"
                    type="number"
                    value={editingPrice.amount}
                    onChange={(e) =>
                      setEditingPrice({
                        ...editingPrice,
                        amount: e.target.value,
                      })
                    }
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
              {editingPrice.hasActiveUsers && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This price has active users. You can only edit the description
                  {!editingPrice.isRecurring && " and quantity"}. Amount and
                  other settings cannot be changed.
                </Alert>
              )}
              <TextField
                label="Description (optional)"
                value={editingPrice.description}
                onChange={(e) =>
                  setEditingPrice({
                    ...editingPrice,
                    description: e.target.value,
                  })
                }
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
              />
              {!editingPrice.isRecurring && (
                <TextField
                  label="Bundle Size (optional)"
                  type="number"
                  value={editingPrice.quantity}
                  onChange={(e) =>
                    setEditingPrice({
                      ...editingPrice,
                      quantity: e.target.value,
                    })
                  }
                  inputProps={{ min: 1 }}
                  fullWidth
                  helperText="Number of uses/sessions included in this purchase"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditPriceDialogOpen(false);
              setEditingPrice(null);
              setError("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSavePriceEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => {
          setDeleteConfirmDialogOpen(false);
          setPriceToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            Are you sure you want to delete this price? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmDialogOpen(false);
              setPriceToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageProducts;
